import { t } from '@glory/i18n';
import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn.ts';
import { IconButton } from './button.tsx';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/** Затемнение под окном: проявляется и гаснет вместе с ним. */
export const overlayClass =
  'fixed inset-0 z-[var(--g-float-z)] bg-overlay data-[state=closed]:pointer-events-none data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in';

/** Окно появляется чуть снизу с увеличением и уходит быстрее, чем появилось; уходящее не ловит нажатия. */
export const dialogMotionClass =
  'data-[state=closed]:pointer-events-none data-[state=closed]:animate-dialog-out data-[state=open]:animate-dialog-in';

/** Модальное окно: заголовок, необязательное описание, содержимое и действия. */
export function DialogContent({
  title,
  description,
  children,
  footer,
  className,
  ...props
}: Omit<ComponentProps<typeof DialogPrimitive.Content>, 'title'> & {
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlayClass} />
      <DialogPrimitive.Content
        className={cn(
          'fixed top-1/2 left-1/2 z-[var(--g-float-z)] flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-lg',
          '-translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border-thick border-strong bg-surface shadow-float',
          dialogMotionClass,
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4">
          <div>
            <DialogPrimitive.Title className="font-display text-title font-bold">
              {title}
            </DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-1 text-muted">
                {description}
              </DialogPrimitive.Description>
            ) : null}
          </div>
          <DialogPrimitive.Close asChild>
            <IconButton label={t('ui.close')} icon={<X />} size="sm" />
          </DialogPrimitive.Close>
        </div>
        <div className="min-h-0 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-border-soft px-5 py-3">{footer}</div>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
